/**
 * useSupportSession — Phase 10.3 client hook
 *
 * Subscribes to the authenticated user's session-control WebSocket channel
 * for support session events (support_session_started, support_session_ended,
 * support_session_rejected). When a support_session_started event arrives,
 * the hook exposes the active session so the UI can auto-join the LiveKit
 * room and render the remote screen viewer.
 *
 * The hook is mounted inside SessionProvider (or a dedicated provider) so
 * it only runs when the user is authenticated.
 */

'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { isSessionControlMessage, sessionControlChannel, type SessionControlMessage } from '@/lib/sessionControl';
import { wsClient } from '@/lib/websocket/TotalAccessWebSocket';

export interface ActiveSupportSession {
  sessionId: string;
  roomName: string;
  deviceId?: number;
  technicianName?: string;
  status: 'active' | 'ended' | 'rejected';
  startedAt: number;
}

export interface EndedSupportSession {
  sessionId?: string;
  reason?: string;
  at: number;
}

interface UseSupportSessionOptions {
  userId: number | null | undefined;
  enabled: boolean;
}

export function useSupportSession({ userId, enabled }: UseSupportSessionOptions) {
  const [activeSession, setActiveSession] = useState<ActiveSupportSession | null>(null);
  const [endedEvent, setEndedEvent] = useState<EndedSupportSession | null>(null);
  const userIdRef = useRef<number | null | undefined>(userId);
  userIdRef.current = userId;

  useEffect(() => {
    if (!enabled) return;
    const id = userIdRef.current;
    if (!id) return;

    const channel = sessionControlChannel(id);

    const handleMessage = async (data: unknown) => {
      if (!isSessionControlMessage(data)) return;
      const msg = data as SessionControlMessage;
      console.log('[useSupportSession] Received:', msg.type, msg);

      switch (msg.type) {
        case 'support_session_started': {
          // The agent has started screen capture and joined the LiveKit room.
          // The UI should auto-join the room as a viewer.
          if ('session_id' in msg && 'room_name' in msg) {
            setActiveSession({
              sessionId: msg.session_id,
              roomName: msg.room_name,
              deviceId: msg.device_id,
              technicianName: 'technician_name' in msg ? msg.technician_name : undefined,
              status: 'active',
              startedAt: Date.now(),
            });
          }
          break;
        }

        case 'support_session_ended': {
          // Session ended (technician ended, user revoked, or TTL expired).
          setActiveSession(null);
          setEndedEvent({
            sessionId: 'session_id' in msg ? msg.session_id : undefined,
            reason: 'reason' in msg ? msg.reason : undefined,
            at: Date.now(),
          });
          break;
        }

        case 'support_session_rejected': {
          // User rejected the consent dialog.
          setActiveSession(null);
          break;
        }
      }
    };

    const unsubscribe = wsClient.subscribe(channel, handleMessage);
    return () => {
      unsubscribe?.();
    };
  }, [enabled]);

  const endSession = useCallback(() => {
    setActiveSession(null);
  }, []);

  return {
    activeSession,
    endedEvent,
    endSession,
  };
}
